import { Component } from '@angular/core'
import { DialogRef } from '@angular/cdk/dialog'
import { selectCases } from 'src/app/state/cases/cases.selectors'
import { take } from 'rxjs/operators'
import { CaseTreeNode } from 'src/app/state/cases/cases.state'
import { Store } from '@ngrx/store'
import { selectPlottingJobSelectionOptions } from 'src/app/state/selection/selection.selectors'
import { environment } from 'src/environments/environment'
import { CaseId, SegObjectPropertyMap } from 'src/app/state/case/case.state'
import { HttpClient } from '@angular/common/http'
import { selectCaseId, selectCaseSegObjectPropertyMap } from 'src/app/state/case/case.selectors'
import { JobSelectionRef } from 'src/app/shared/model/jobs'

interface TreeNode {
    /** Display label in the dialog tree (and groups preview). */
    label: string
    show: boolean
    selected: boolean
    isTopLevel?: boolean
    nodes: TreeNode[]
    details?: any
    /** For Selections leaves: same ref shape as RCN/NEN. */
    selectionRef?: JobSelectionRef
}

@Component({
    selector: 'app-create-group-dialog',
    templateUrl: './create-group-dialog.component.html',
    styleUrls: ['./create-group-dialog.component.scss'],
})
export class CreateGroupDialogComponent {
    nodes: TreeNode[] = [
        {
            label: 'Cohort',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
        {
            label: 'Selections',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
        {
            label: 'Categorical property',
            show: false,
            selected: false,
            isTopLevel: true,
            nodes: [],
        },
    ]
    cohorts: { id: number; name: string }[] = []
    readonly currentSegObjectPropertyMap$ = this.store.select(selectCaseSegObjectPropertyMap)
    private readonly baseUrl = environment.apiUrl
    private caseId: CaseId

    constructor(
        private readonly dialogRef: DialogRef<any[]>,
        private readonly http: HttpClient,
        private readonly store: Store
    ) {
        this.store
            .select(selectCaseId)
            .pipe(take(1))
            .subscribe(caseId => {
                this.caseId = caseId
            })

        this.store
            .select(selectCases)
            .pipe(take(1))
            .subscribe((cases: CaseTreeNode[]) => {
                this.cohorts = cases
                    .filter(c => c.children?.length)
                    .map(c => ({ id: c.id, name: c.name }))

                const cohortNode = this.nodes.find(n => n.label === 'Cohort')
                if (cohortNode) {
                    cohortNode.nodes = this.cohorts.map(cohort => ({
                        label: cohort.name,
                        show: false,
                        selected: false,
                        nodes: [],
                    }))
                }
            })

        this.store
            .select(selectPlottingJobSelectionOptions)
            .pipe(take(1))
            .subscribe(selectionOptions => {
                const selectionNode = this.nodes.find(n => n.label === 'Selections')
                if (selectionNode) {
                    selectionNode.nodes = selectionOptions.map(option => ({
                        label: option.name,
                        show: false,
                        selected: false,
                        nodes: [],
                        selectionRef: {
                            id: option.id,
                            type: option.type,
                            ...(option.path?.length ? { path: option.path } : {}),
                        },
                    }))
                }
            })

        this.currentSegObjectPropertyMap$.subscribe(segObjectPropertyMap => {
            if (segObjectPropertyMap) {
                this.http
                    .get<SegObjectPropertyMap[]>(
                        `${this.baseUrl}/datasets/${this.caseId}/mappings/${
                            segObjectPropertyMap.id
                        }/categoricalProperties?_=${Date.now()}`
                    )
                    .subscribe({
                        next: response => {
                            const categoricalPropertyNode = this.nodes.find(
                                n => n.label === 'Categorical property'
                            )
                            if (categoricalPropertyNode) {
                                categoricalPropertyNode.nodes = response.map(property => ({
                                    label: property.name,
                                    show: false,
                                    selected: false,
                                    nodes: [],
                                    details: property,
                                }))
                            }
                        },
                        error: error => {
                            console.error('Error fetching categorical properties:', error)
                        },
                    })
            }
        })
    }

    hasChildren(node: TreeNode): boolean {
        return node.nodes && node.nodes.length > 0
    }

    toggleNode(node: TreeNode) {
        node.show = !node.show
    }

    selectSelection(node: TreeNode) {
        node.selected = !node.selected

        if (node.isTopLevel && node.nodes.length > 0) {
            node.nodes.forEach(child => {
                child.selected = node.selected
            })
        }
    }

    cancel(): void {
        this.dialogRef.close(undefined)
    }

    confirm(): void {
        this.dialogRef.close(
            this.nodes
                .map(node => ({
                    id: node.label,
                    nodes: node.nodes
                        .filter(n => n.selected)
                        .map(n => this.serializeSelectedNode(node.label, n)),
                }))
                .filter(n => n.nodes.length > 0)
        )
    }

    /** Selections: flat { id, type, path? } like NEN. Others keep prior shape for UI/backend. */
    private serializeSelectedNode(
        groupLabel: string,
        node: TreeNode
    ): (JobSelectionRef & { label?: string }) | { id: string; details?: any; label?: string } {
        if (groupLabel === 'Selections' && node.selectionRef) {
            return {
                id: node.selectionRef.id,
                type: node.selectionRef.type,
                ...(node.selectionRef.path?.length ? { path: node.selectionRef.path } : {}),
                label: node.label, // UI preview only; stripped in getPayload
            }
        }
        return {
            id: node.label,
            details: node.details,
            label: node.label,
        }
    }
}
